/**
 * Die Sicherungskopie: alles, was auf diesem Gerät liegt, in einer Datei.
 *
 * Nicht zu verwechseln mit dem Abgleich. Der hält zwei Geräte gleich und
 * überträgt dafür nur Kopfdaten — Tiefenprofile bleiben draussen, weil sie 95
 * Prozent der Datenmenge ausmachen und sich nie ändern. Eine Sicherung hat den
 * umgekehrten Zweck: Wenn das Telefon im Hafenbecken liegt, soll **alles**
 * zurückkommen, Profile eingeschlossen.
 *
 * Gepackt wird nicht. Gemessen am Bestand: 995 Tauchgänge samt Profilen sind
 * 2.9 MB roher JSON — klein genug für beide Anbieter, und das erspart es,
 * Dropbox- und Drive-Adapter auf Binärdaten umzubauen. Wo eine Fehlerquelle
 * nichts einbringt, gehört sie nicht hinein.
 *
 * Der Check-in-Tresor wandert mit, aber wie überall verschlüsselt: `payload_enc`
 * bleibt Geheimtext, das Passwort liegt nie in der Datei.
 */

export const FORMAT = 'tauchlogbuch-sicherung/1';

/**
 * Die Speicher einer Sicherung, in der Reihenfolge, in der sie zurückkommen.
 *
 * Tauchgänge vor ihren Profilen: Bricht das Zurückholen dazwischen ab, liegen
 * dann höchstens Tauchgänge ohne Messpunkte da — nie Messpunkte ohne
 * Tauchgang.
 *
 * `grabsteine` ist hier der **Merkzettel gelöschter Sätze** (`SPEICHER.grabsteine`,
 * Schlüssel `id`). Nicht zu verwechseln mit den Grabsteinen des Check-ins:
 * Die liegen im Tresor unter dem Schlüssel `grabsteine` und reisen als
 * `checkinGrabsteine` mit. Zwei Dinge, ein Wort — wer sie vertauscht, spielt
 * beim Zurückholen die falsche Liste in den falschen Speicher.
 */
export const BEREICHE = ['dives', 'samples', 'sites', 'trips', 'buddies', 'gear',
  'checkin', 'grabsteine'];

/**
 * Eine Sicherung zusammenstellen.
 *
 * `stand` bringt je Bereich eine Liste mit; was fehlt, wird zur leeren Liste.
 * Der Tresor steht daneben, weil er kein Satzspeicher ist, sondern ein
 * einzelner Eintrag.
 */
export function baueSicherung(stand = {}) {
  const sicherung = {
    format: FORMAT,
    erzeugt: stand.erzeugt ?? null,
    geraet: stand.geraet ?? null,
    tresor: stand.tresor ?? null,
    // Die Grabsteine des Check-ins — sie liegen im Tresor und nicht in einem
    // eigenen Speicher, deshalb stehen sie hier und nicht in BEREICHE.
    checkinGrabsteine: Array.isArray(stand.checkinGrabsteine) ? stand.checkinGrabsteine : [],
  };
  for (const bereich of BEREICHE) {
    sicherung[bereich] = Array.isArray(stand[bereich]) ? stand[bereich] : [];
  }
  return sicherung;
}

/**
 * Ist das eine Sicherung dieser App?
 *
 * Beim Zurückholen wird der ganze Bestand ersetzt. Eine falsche Datei darf
 * deshalb nicht halb eingespielt werden, sondern muss vorher auffallen — und
 * die Meldung soll sagen, was stattdessen dastand.
 */
export function pruefeSicherung(daten) {
  if (!daten || typeof daten !== 'object' || Array.isArray(daten)) {
    throw new Error('Das ist keine Sicherungskopie.');
  }
  if (daten.format !== FORMAT) {
    // Die häufigste Verwechslung: das Bundle aus der Pipeline.
    const hinweis = daten.format === 'tauchlogbuch/1'
      ? ' Das ist ein Bundle aus der Pipeline — es gehört unter „Laden".'
      : '';
    throw new Error(`Unbekanntes Format: ${daten.format ?? 'keines'}. `
      + `Erwartet wird ${FORMAT}.${hinweis}`);
  }
  for (const bereich of BEREICHE) {
    if (daten[bereich] !== undefined && !Array.isArray(daten[bereich])) {
      throw new Error(`Der Bereich „${bereich}" ist beschädigt.`);
    }
  }
  return daten;
}

/**
 * Was in der Sicherung steckt — für die Rückfrage vor dem Überschreiben.
 *
 * Wer „Aus der Cloud holen" drückt, soll vorher lesen, was ihn erwartet.
 * „Ersetzt deinen Bestand" ohne Zahlen ist keine Grundlage für ein Ja.
 */
export function zusammenfassung(daten) {
  const liste = (bereich) => (Array.isArray(daten?.[bereich]) ? daten[bereich] : []);
  const profile = liste('samples');
  return {
    tauchgaenge: liste('dives').length,
    profile: profile.length,
    messpunkte: profile.reduce((summe, p) => summe + (p.werte?.length ?? 0), 0),
    plaetze: liste('sites').length,
    reisen: liste('trips').length,
    buddys: liste('buddies').length,
    ausruestung: liste('gear').length,
    unterlagen: liste('checkin').length,
    grabsteine: liste('grabsteine').length,
    tresor: Boolean(daten?.tresor),
    erzeugt: daten?.erzeugt ?? null,
    geraet: daten?.geraet ?? null,
  };
}

/** Dieselbe Zusammenfassung als Satz, den man vorlesen kann. */
export function beschreibe(daten) {
  const z = zusammenfassung(daten);
  const teile = [
    `${z.tauchgaenge} ${z.tauchgaenge === 1 ? 'Tauchgang' : 'Tauchgänge'}`,
    `${z.profile} Profile`,
    `${z.plaetze} Plätze`,
  ];
  if (z.unterlagen) teile.push(`${z.unterlagen} Unterlagen`);
  const wann = z.erzeugt ? new Date(z.erzeugt).toLocaleString('de-CH') : 'unbekannt';
  return `${teile.join(', ')} · gesichert am ${wann}`;
}
